/**
 * Public marketing landing page rendered at `/` on the web.
 *
 * Content is intentionally minimal per spec:
 *   - Dark navy background (#0B1824)
 *   - "SMARTERHOOPS" wordmark (SMARTER white, HOOPS orange #ffa133)
 *   - Tagline "Know The Game."
 *   - App Store + Google Play download badges
 *
 * The native app (Capacitor) must never render this page — if it ever
 * lands here (e.g., a future deep link), bounce to /dashboard so the
 * device-UUID auth flow stays the only entry point.
 */
import { Capacitor } from '@capacitor/core';
import { Navigate } from 'react-router-dom';

const NAVY = '#0B1824';

const APP_STORE_URL =
  process.env.REACT_APP_APP_STORE_URL ||
  'https://apps.apple.com/app/smarterhoops';
const PLAY_STORE_URL =
  process.env.REACT_APP_PLAY_STORE_URL ||
  'https://play.google.com/store/apps/details?id=com.smarterhoops.app';

export default function LandingPage() {
  let native = false;
  try {
    native = Capacitor.isNativePlatform();
  } catch {
    /* ignore — assume web */
  }
  if (native) return <Navigate to="/dashboard" replace />;

  return (
    <div
      data-testid="landing-page"
      className="min-h-screen w-full flex flex-col items-center justify-center px-6 py-12"
      style={{ backgroundColor: NAVY }}
    >
      <img
        src="/smarterhoops-app-logo.png"
        alt="SmarterHoops"
        data-testid="landing-hero-logo"
        className="block w-[280px] sm:w-[360px] md:w-[440px] h-auto select-none pointer-events-none"
        draggable="false"
      />

      <p
        data-testid="landing-tagline"
        className="mt-2 text-base sm:text-lg uppercase font-semibold text-slate-300"
        style={{
          fontFamily: "'Barlow Condensed', sans-serif",
          letterSpacing: '0.32em',
        }}
      >
        Know The Game.
      </p>

      <div
        data-testid="landing-store-badges"
        className="mt-14 flex flex-col sm:flex-row items-center gap-5 sm:gap-7"
      >
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="landing-app-store-badge"
          className="inline-block transition-transform hover:scale-[1.03] active:scale-[.97]"
          aria-label="Download on the App Store"
        >
          <img
            src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us"
            alt="Download on the App Store"
            className="block h-14 sm:h-[60px] w-auto"
            draggable="false"
          />
        </a>
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="landing-google-play-badge"
          className="inline-block transition-transform hover:scale-[1.03] active:scale-[.97]"
          aria-label="Get it on Google Play"
        >
          <img
            src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
            alt="Get it on Google Play"
            className="block h-[68px] sm:h-[88px] w-auto"
            draggable="false"
          />
        </a>
      </div>
    </div>
  );
}
